import React, { useState } from 'react';
import API_BASE_URL from '../services/api';
import { Mail, User, ArrowRight, LogIn } from 'lucide-react';
import useDarkMode from '../hooks/useDarkMode';
import ThemeToggle from './ThemeToggle';
import OtpPopup from './OtpPopup';

export default function LoginForm({ onLogin }) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [step, setStep] = useState(1);
  const [existingUser, setExistingUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const { darkMode, toggle: toggleDark } = useDarkMode();

  // OTP state
  const [showOtp, setShowOtp] = useState(false);
  const [otpUserId, setOtpUserId] = useState(null);
  const [otpEmail, setOtpEmail] = useState('');
  const [devOtp, setDevOtp] = useState(null);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users/by-email/${encodeURIComponent(email)}`);
      if (res.ok) {
        const data = await res.json();
        setUsername(data.username || '');
        setExistingUser(true);
      } else if (res.status === 404) {
        setUsername('');
        setExistingUser(false);
      } else {
        console.error('Email kontrolü sırasında hata');
        return;
      }
    } catch (err) {
      console.error('Email kontrolü sırasında hata:', err);
      return;
    } finally {
      setLoading(false);
    }
    setStep(2);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !email.trim()) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email }),
      });
      const data = await res.json();

      if (!res.ok) {
        alert(data.message || 'OTP gönderme hatası.');
        return;
      }

      setOtpUserId(data.userId);
      setOtpEmail(data.email);
      setDevOtp(data.devOtp || null);
      setShowOtp(true);
    } catch (err) {
      console.error('OTP gönderme hatası:', err);
      alert('OTP gönderme sırasında bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerified = (token, user) => {
    setShowOtp(false);
    onLogin({ ...user, token });
  };

  const handleOtpCancel = () => {
    setShowOtp(false);
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '1.5rem',
      background: 'var(--bg-primary)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '440px',
        animation: 'fadeIn 0.4s ease-out',
      }}>
        {/* Theme Toggle */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <ThemeToggle darkMode={darkMode} onToggle={toggleDark} />
        </div>

        {/* Logo / Brand */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img
            src="/favicon.svg"
            alt="Pupl Logo"
            style={{
              width: '64px',
              height: '64px',
              marginBottom: '1rem',
            }}
          />
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: 'var(--text-primary)',
            letterSpacing: '-0.03em',
            margin: 0,
          }}>
            Pupl
          </h1>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '1rem',
            marginTop: '0.25rem',
          }}>
            Etkinliklerini kolayca planla ve paylaş
          </p>
        </div>

        {/* Form Card */}
        <form
          onSubmit={step === 1 ? handleEmailSubmit : handleLogin}
          style={{
            background: 'var(--bg-elevated)',
            padding: '2rem',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-xl)',
            border: '1px solid var(--border-color)',
          }}
        >
          <h2 style={{
            textAlign: 'center',
            color: 'var(--text-primary)',
            marginBottom: '1.75rem',
            fontSize: '1.375rem',
            fontWeight: '600',
          }}>
            {step === 1 ? 'Giriş' : (existingUser ? 'Hoş Geldiniz' : 'Hesap Oluştur')}
          </h2>

          {/* Email Field */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: '500',
              color: 'var(--text-secondary)',
              marginBottom: '0.5rem',
              fontSize: '0.875rem',
            }}>
              <Mail size={14} />
              E-posta Adresi
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ornek@pupl.com"
              required
              disabled={step === 2}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: '1.5px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                fontSize: '1rem',
                background: step === 2 ? 'var(--neutral-100)' : 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                outline: 'none',
                transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--primary-400)';
                e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border-color)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {step === 2 && (
            <div style={{ animation: 'slideUp 0.3s ease-out' }}>
              {/* Username */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '500',
                  color: 'var(--text-secondary)',
                  marginBottom: '0.5rem',
                  fontSize: '0.875rem',
                }}>
                  <User size={14} />
                  Kullanıcı Adı
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="kullaniciadi"
                  required
                  readOnly={existingUser}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1.5px solid var(--border-color)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '1rem',
                    background: existingUser ? 'var(--neutral-100)' : 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
                  }}
                  onFocus={(e) => {
                    if (!existingUser) {
                      e.target.style.borderColor = 'var(--primary-400)';
                      e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
                    }
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--border-color)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.85rem',
              background: 'var(--primary-600)',
              color: '#fff',
              fontSize: '1rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: loading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'background var(--transition-fast)',
              opacity: loading ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (!loading) e.currentTarget.style.background = 'var(--primary-700)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--primary-600)';
            }}
          >
            {step === 1 ? (
              <>Devam Et <ArrowRight size={18} /></>
            ) : existingUser ? (
              <>Giriş Yap <LogIn size={18} /></>
            ) : (
              <>Hesap Oluştur</>
            )}
          </button>
        </form>

        {/* OTP Popup */}
        {showOtp && (
          <OtpPopup
            email={otpEmail}
            userId={otpUserId}
            devOtp={devOtp}
            onVerified={handleOtpVerified}
            onCancel={handleOtpCancel}
          />
        )}
      </div>
    </div>
  );
}
